import { clamp, clone, minBy } from 'lodash';
import { StrikeZoneType } from '../enums/machine.enums';
import { PlateH, PlateOutcome, PlateV } from '../enums/plate.enums';
import { IPlateSummary } from '../interfaces/i-plate-config';
import { IStrikeZone } from '../interfaces/i-strike-zone';
import { IPlateLoc, ITrajectory } from '../interfaces/pitches';
import { DEFAULT_PLATE } from '../interfaces/pitches/i-plate-loc';
import { FT_TO_INCHES, isBetween } from './math.utilities';
import { TrajHelper } from './trajectory.helper';

/**
 * represents the distance from the tip of the home plate to the front edge (closer to pitcher)
 * from MLB rules: https://www.mlb.com/glossary/rules/field-dimensions
 * "Home plate is a 17-inch square... with two of the corners removed"
 *
 * As of Jan 2026 and ABS SZ, this is still used to determine break calculations (in contrast
 * to plate location and ball/strike calls which use PLATE_MIDDLE)
 * */
const PLATE_EDGE_IN = 17;
export const PLATE_EDGE_FT = PLATE_EDGE_IN / FT_TO_INCHES;

/**
 * represents the distance from the tip of home plate to the middle of the plate
 * As of Jan 2026 and ABS SZ, this is used instead of PLATE_EDGE to calculate flight time
 * for the purposes of plate location and ball/strike calls
 */
const PLATE_MIDDLE_IN = PLATE_EDGE_IN / 2; // 8.5 inches
export const PLATE_MIDDLE_FT = PLATE_MIDDLE_IN / FT_TO_INCHES;

const STRIKEZONE_HEIGHT_IN = 19.5;
export const BALL_DIAMETER_IN = 2.94;

export class PlateHelper {
  static getDynamicStrikeZone(
    type: StrikeZoneType,
    playerHeightFt: number | undefined
  ): IStrikeZone {
    try {
      switch (type) {
        case StrikeZoneType.MlbAbs: {
          return this.getMlbAbsStrikeZone(playerHeightFt);
        }

        case StrikeZoneType.Stringer: {
          return this.getStringerStrikeZone(playerHeightFt);
        }

        default: {
          return VISIBLE_STRIKEZONE;
        }
      }
    } catch (e) {
      console.error(`getDynamicStrikeZone failed`, e);
      return VISIBLE_STRIKEZONE;
    }
  }

  static getRawStrikezone = (config: {
    playerHeightIn: number;

    plateCenterFt: IPlateLoc;

    szWidthIn: number;
    szHeightIn: number;
  }): IStrikeZone => {
    const halfWidthFt = config.szWidthIn / 2 / FT_TO_INCHES;
    const halfHeightFt = config.szHeightIn / 2 / FT_TO_INCHES;

    const output: IStrikeZone = {
      /** relevant for Stringer strike zones to find the closest one to player's height */
      height_in: config.playerHeightIn,

      bottom_ft: config.plateCenterFt.plate_z - halfHeightFt,
      top_ft: config.plateCenterFt.plate_z + halfHeightFt,

      left_ft: config.plateCenterFt.plate_x - halfWidthFt,
      right_ft: config.plateCenterFt.plate_x + halfWidthFt,

      plate_ft: config.plateCenterFt,
    };

    return output;
  };

  // gets the pitch def's plate and then calls getPlateSummary
  static getPitchSummary = (
    pitch: {
      plate_loc_backup?: IPlateLoc;
      traj: ITrajectory;
    },
    // uses the default strikezone if not provided
    szDef?: IStrikeZone
  ): IPlateSummary => {
    return this.getPlateSummary(
      pitch.plate_loc_backup ?? TrajHelper.getPlateLoc(pitch.traj),
      szDef ?? VISIBLE_STRIKEZONE
    );
  };

  // calls getSummary w/o hitter
  static getPlateSummary = (
    plateFt: IPlateLoc,
    szDef?: IStrikeZone
  ): IPlateSummary => {
    return this.getSummary({
      szDef: szDef ?? VISIBLE_STRIKEZONE,
      plateFt: plateFt,
    });
  };

  private static getSort(
    x: {
      cell: PlateH;
      isOutsideLeft?: boolean;
    },
    z: {
      cell: PlateV;
      isOutsideTop?: boolean;
    }
  ) {
    return 10 * this.getSortZ(z) + this.getSortX(x);
  }

  //left => right
  private static getSortX = (config: {
    cell: PlateH;
    isOutsideLeft?: boolean;
  }) => {
    switch (config.cell) {
      case PlateH.L:
        return 4;
      case PlateH.C:
        return 3;
      case PlateH.R:
        return 2;
      default:
        // outside to the left
        if (config.isOutsideLeft) {
          return 5;
        }

        // outside to the right
        return 1;
    }
  };

  //top => bottom
  private static getSortZ = (config: {
    cell: PlateV;
    isOutsideTop?: boolean;
  }) => {
    switch (config.cell) {
      case PlateV.T:
        return 4;
      case PlateV.M:
        return 3;
      case PlateV.B:
        return 2;
      default:
        // outside to the top
        if (config.isOutsideTop) {
          return 5;
        }

        // outside to the bottom
        return 1;
    }
  };

  static isStrike(config: { szDef: IStrikeZone; plateFt: IPlateLoc }) {
    const bufferFt = BALL_DIAMETER_IN / FT_TO_INCHES / 2;

    return this.isPlateInZone({
      plateFt: config.plateFt,
      szDef: config.szDef,
      bufferFt: bufferFt,
    });
  }

  static getSummary = (config: {
    szDef: IStrikeZone;
    plateFt: IPlateLoc;
  }): IPlateSummary => {
    const { szDef, plateFt } = config;

    const isStrike = this.isStrike(config);

    if (!isStrike) {
      const outsideV = !isBetween(
        plateFt.plate_z,
        szDef.bottom_ft,
        szDef.top_ft
      );

      const outsideH = !isBetween(
        plateFt.plate_x,
        szDef.left_ft,
        szDef.right_ft
      );

      /**
       * BA_TL   BA_TR
       * BA_BL   BA_BR
       */

      /** T / B */
      const zCell = (() => {
        const half = Math.abs(szDef.top_ft - szDef.bottom_ft) / 2;
        return plateFt.plate_z > szDef.bottom_ft + half ? PlateV.T : PlateV.B;
      })();

      /** R / L */
      const xCell = (() => {
        const half = Math.abs(szDef.right_ft - szDef.left_ft) / 2;
        return plateFt.plate_x > szDef.left_ft + half ? PlateH.R : PlateH.L;
      })();

      const output: IPlateSummary = {
        outcome: PlateOutcome.Ball,
        vertical: zCell,
        horizontal: xCell,
        grid: `${PlateOutcome.Ball}_${zCell}${xCell}`,
        grid_x: -1,
        grid_z: -1,
        szDef: szDef,
        plateFt: plateFt,
        sort: this.getSort(
          {
            cell: outsideH ? PlateH.Unknown : xCell,
            isOutsideLeft: xCell === PlateH.L,
          },
          {
            cell: outsideV ? PlateV.Unknown : zCell,
            isOutsideTop: zCell === PlateV.T,
          }
        ),
      };

      return output;
    }

    /** B / M / T */
    const zDef: { cell: PlateV; grid_z: number } = (() => {
      const szHeightFt = Math.abs(szDef.top_ft - szDef.bottom_ft);

      // half the height of one third of the SZ
      const half3rdFt = szHeightFt / 3 / 2;

      // midpoint between top and bottom for horizontal
      const midpointFt = (szDef.top_ft + szDef.bottom_ft) / 2;

      // how far up (from the bottom) to draw the dot
      const gridPercent = clamp(
        (plateFt.plate_z - szDef.bottom_ft) / szHeightFt,
        0,
        1
      );

      if (plateFt.plate_z > midpointFt + half3rdFt) {
        return {
          cell: PlateV.T,
          grid_z: gridPercent,
        };
      }

      if (plateFt.plate_z < midpointFt - half3rdFt) {
        return {
          cell: PlateV.B,
          grid_z: gridPercent,
        };
      }

      return {
        cell: PlateV.M,
        grid_z: gridPercent,
      };
    })();

    /** L / C / R */
    const xDef: { cell: PlateH; grid_x: number } = (() => {
      const szWidthFt = Math.abs(szDef.right_ft - szDef.left_ft);

      // half the width of one third of the SZ
      const half3rdFt = szWidthFt / 3 / 2;

      // always 0 for horizontal
      const midpointFt = 0;

      // how far over (from the left) to draw the dot
      const gridPercent = clamp(
        (plateFt.plate_x - szDef.left_ft) / szWidthFt,
        0,
        1
      );

      if (plateFt.plate_x > midpointFt + half3rdFt) {
        return {
          cell: PlateH.R,
          grid_x: gridPercent,
        };
      }

      if (plateFt.plate_x < midpointFt - half3rdFt) {
        return {
          cell: PlateH.L,
          grid_x: gridPercent,
        };
      }

      return {
        cell: PlateH.C,
        grid_x: gridPercent,
      };
    })();

    const output: IPlateSummary = {
      outcome: PlateOutcome.Strike,
      grid: `${PlateOutcome.Strike}_${zDef.cell}${xDef.cell}`,

      vertical: zDef.cell,
      grid_z: zDef.grid_z,

      horizontal: xDef.cell,
      grid_x: xDef.grid_x,
      szDef: szDef,
      plateFt: plateFt,
      sort: this.getSort({ cell: xDef.cell }, { cell: zDef.cell }),
    };

    return output;
  };

  static isPlateInZone(config: {
    plateFt: IPlateLoc;
    szDef: IStrikeZone;
    bufferFt: number;
  }): boolean {
    if (config.plateFt.plate_x > config.szDef.right_ft + config.bufferFt) {
      // outside right
      return false;
    }

    if (config.plateFt.plate_x < config.szDef.left_ft - config.bufferFt) {
      // outside left
      return false;
    }

    if (config.plateFt.plate_z > config.szDef.top_ft + config.bufferFt) {
      // outside above
      return false;
    }

    if (config.plateFt.plate_z < config.szDef.bottom_ft - config.bufferFt) {
      // outside below
      return false;
    }

    return true;
  }
  /**
   *
   * @param playerHeightFt will be used to determine strike zone that's the closest match
   * @returns
   */
  static getStringerStrikeZone = (
    playerHeightFt: number | undefined
  ): IStrikeZone => {
    try {
      if (playerHeightFt === undefined || isNaN(playerHeightFt)) {
        return clone(VISIBLE_STRIKEZONE);
      }

      /** if there is a failure to find, the closest zone will be used */
      const playerHeightIn = Math.round(playerHeightFt * 12);

      const closest = minBy(STRINGER_STRIKEZONES, (m) =>
        Math.abs(m.height_in - playerHeightIn)
      );

      if (!closest) {
        throw new Error('Failed to find closest Stringer strike zone');
      }

      return clone(closest);
    } catch (e) {
      console.error(e);
      return clone(VISIBLE_STRIKEZONE);
    }
  };

  static getMlbAbsStrikeZone = (
    playerHeightFt: number | undefined
  ): IStrikeZone => {
    try {
      if (playerHeightFt === undefined || isNaN(playerHeightFt)) {
        return clone(VISIBLE_STRIKEZONE);
      }

      const topFt = 0.535 * playerHeightFt;
      const bottomFt = 0.27 * playerHeightFt;

      return this.getRawStrikezone({
        playerHeightIn: playerHeightFt * FT_TO_INCHES,
        plateCenterFt: {
          plate_x: 0,
          plate_z: (topFt + bottomFt) / 2,
        },
        szWidthIn: PLATE_EDGE_IN,
        szHeightIn: (topFt - bottomFt) * FT_TO_INCHES,
      });
    } catch (e) {
      console.error(e);
      return clone(VISIBLE_STRIKEZONE);
    }
  };
}

// for plots and aiming widgets
export const VISIBLE_STRIKEZONE = PlateHelper.getRawStrikezone({
  playerHeightIn: 0,
  plateCenterFt: DEFAULT_PLATE,
  szWidthIn: PLATE_EDGE_IN,
  szHeightIn: STRIKEZONE_HEIGHT_IN,
});

/**
 * index 0 = player height in inches
 * index 1 = strike zone top in inches
 * index 2 = strike zone bottom in inches
 */
const STRINGER_TABLE: number[][] = [
  // [60, 40.3, 20.4],
  [65, 38, 17.3],
  [66, 36.4, 16.8],
  [67, 40.4, 18.6],
  [68, 38.6, 17.9],
  [69, 39.3, 18.1],
  [70, 39.7, 18.3],
  [71, 39.8, 18.3],
  [72, 40.4, 18.7],
  [73, 41, 18.9],
  [74, 41.7, 19.4],
  [75, 42.3, 19.7],
  [76, 42.7, 20],
  [77, 43.2, 20.2],
  [78, 43.4, 20.4],
  [79, 44.7, 21],
  [80, 44.1, 20.5],
  [81, 44.7, 20.8],
  [82, 47.8, 22.6],
  [83, 48.1, 22.1],
];

export const STRINGER_STRIKEZONES: IStrikeZone[] = STRINGER_TABLE.map(
  ([playerHeightIn, topIn, bottomIn]) => {
    const szMiddleIn = (topIn + bottomIn) / 2;

    return PlateHelper.getRawStrikezone({
      playerHeightIn: playerHeightIn,
      plateCenterFt: {
        plate_x: 0,
        plate_z: szMiddleIn / FT_TO_INCHES,
      },
      szWidthIn: PLATE_EDGE_IN + BALL_DIAMETER_IN,
      szHeightIn: topIn - bottomIn + BALL_DIAMETER_IN,
    });
  }
);
